/**
 * Application configuration
 * Centralized configuration with environment variable support
 */

/**
 * Required build-time variables (Phase 17 PRD-01): no hard-coded pool, client or API fallbacks,
 * so a build without them fails loudly at startup instead of talking to the wrong environment.
 * scripts/build.mjs checks the same names before `react-scripts build` runs.
 */
export function requireEnv(name: string, value: string | undefined): string {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    throw new Error(`${name} is not set. Add it to the .env file for this environment (see .env.example).`);
  }
  return trimmed;
}

// Cognito configuration
export const cognitoConfig = {
  userPoolId: requireEnv('REACT_APP_COGNITO_USER_POOL_ID', process.env.REACT_APP_COGNITO_USER_POOL_ID),
  clientId: requireEnv('REACT_APP_COGNITO_CLIENT_ID', process.env.REACT_APP_COGNITO_CLIENT_ID),
  region: process.env.REACT_APP_COGNITO_REGION || 'us-east-1',
} as const;

// Support contact shown when a user can't complete sign-up (EX-02)
export const supportConfig = {
  email: process.env.REACT_APP_SUPPORT_EMAIL || '',
} as const;

/**
 * REACT_APP_API_BASE_URL is required (Phase 3): there is no hard-coded fallback,
 * so a build without it fails loudly at startup instead of calling a wrong host.
 */
export function requireApiBaseUrl(value: string | undefined = process.env.REACT_APP_API_BASE_URL): string {
  return requireEnv('REACT_APP_API_BASE_URL', (value || '').trim().replace(/\/+$/, ''));
}

// API configuration
export const apiConfig = {
  baseUrl: requireApiBaseUrl(),
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10),
} as const;

/**
 * Phase 22 Pelora Billing (billing-edi HttpApi). Optional: needed only when the tenant's
 * `billingEdi` flag is on, so a build without it starts normally and the EDI screens stay hidden
 * (ediClient throws EDI_NOT_CONFIGURED if a call is attempted without it).
 */
export function resolveEdiApiBaseUrl(value: string | undefined = process.env.REACT_APP_EDI_API_BASE_URL): string {
  return (value || '').trim().replace(/\/+$/, '');
}

export const ediApiConfig = {
  baseUrl: resolveEdiApiBaseUrl(),
} as const;

// Feature flags
export const featureFlags = {
  enableMfa: process.env.REACT_APP_ENABLE_MFA !== 'false',
  enableDevMode: process.env.REACT_APP_DEV_MODE === 'true',
  enableDebugLogging: process.env.REACT_APP_DEBUG_LOGGING === 'true',
} as const;

// Application metadata
export const appConfig = {
  name: 'Pelora Health',
  version: process.env.REACT_APP_VERSION || '0.1.0',
  environment: process.env.NODE_ENV || 'development',
} as const;

// Storage keys
export const storageKeys = {
  accessToken: 'accessToken',
  idToken: 'idToken',
  refreshToken: 'refreshToken',
  userAttributes: 'userAttributes',
  tenantId: 'tenantId',
  providerType: 'providerType',
  sessionEmail: 'signInEmail',
} as const;

// API error messages
export const apiErrorMessages = {
  default: 'An unexpected error occurred. Please try again later.',
  network: 'Network error. Please check your internet connection and try again.',
  timeout: 'The request timed out. Please try again later.',
  unauthorized: 'Your session has expired. Please log in again.',
  forbidden: 'You do not have permission to perform this action.',
  notFound: 'The requested resource could not be found.',
  serverError: 'A server error occurred. Our team has been notified.',
  validation: 'Please check your input and try again.',
} as const;

// Pagination defaults
export const paginationDefaults = {
  pageSize: 10,
  maxPageSize: 100,
} as const;

// Date format strings
export const dateFormats = {
  display: 'MM/DD/YYYY',
  displayWithTime: 'MM/DD/YYYY h:mm A',
  api: 'YYYY-MM-DD',
  apiWithTime: 'YYYY-MM-DDTHH:mm:ss',
} as const;

// Export combined config for convenience
export const config = {
  cognito: cognitoConfig,
  api: apiConfig,
  ediApi: ediApiConfig,
  features: featureFlags,
  app: appConfig,
  storage: storageKeys,
  errors: apiErrorMessages,
  pagination: paginationDefaults,
  dateFormats,
} as const;

export default config;
